import { useEffect, useRef } from 'react';

interface UseServerHealthCheckOptions {
  url: string;
  endpoint: string;
  onStatusChange: (status: 'online' | 'offline' | 'checking') => void;
  currentStatus: 'online' | 'offline' | 'checking';
  /** Timeout in ms before giving up on 'checking' state (default: 30000) */
  initialTimeout?: number;
}

/**
 * Custom hook for server health checking with adaptive polling
 * Uses Next.js API proxy to avoid browser console network errors
 * Polls more frequently when offline (15s), less frequently when online (30s)
 * Times out to 'offline' if still 'checking' after initialTimeout (default 30s)
 */
export function useServerHealthCheck({
  url,
  endpoint,
  onStatusChange,
  currentStatus,
  initialTimeout = 30000,
}: UseServerHealthCheckOptions) {
  const hasChecked = useRef(false);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        // Use Next.js API proxy to avoid browser network error logs
        // Timeout extended to 35s to allow for TTS generation blocking
        const response = await fetch('/api/servers', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'health-check', url, endpoint }),
          signal: AbortSignal.timeout(35000),
        });
        hasChecked.current = true;
        const data = await response.json();
        onStatusChange(data.status === 'online' ? 'online' : 'offline');
      } catch {
        hasChecked.current = true;
        onStatusChange('offline');
      }
    };

    checkStatus();

    // Offline: every 15 seconds, Online: every 30 seconds
    const interval = setInterval(
      checkStatus,
      currentStatus === 'offline' ? 15000 : 30000
    );

    // Safety timeout: if still 'checking' after initialTimeout, set to 'offline'
    const safetyTimeout = setTimeout(() => {
      if (!hasChecked.current && currentStatus === 'checking') {
        onStatusChange('offline');
      }
    }, initialTimeout);

    return () => {
      clearInterval(interval);
      clearTimeout(safetyTimeout);
    };
  }, [url, endpoint, onStatusChange, currentStatus, initialTimeout]);
}
